import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Lock, Loader } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

export default function SendCrypto() {
  const navigate = useNavigate();
  const { cryptoType } = useParams();
  const [params] = useSearchParams();
  const { user } = useUser();

  const [amount, setAmount] = useState('');
  const [walletID, setWalletID] = useState(params.get('address') || '');
  const [pin, setPin] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter valid amount');
      return;
    }
    if (!walletID) {
      toast.error('Enter wallet address');
      return;
    }
    setShowPinModal(true);
  };

  const handleSubmitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) {
      toast.error('PIN must be 6 digits');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/payment/SendCrypto-web`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.telegramName,
          amount,
          addres: walletID, // Note: API has typo "addres"
          coin: cryptoType,
          pin,
        }),
      });

      const data = await res.json();
      if (res.status === 200) {
        toast.success('Transaction successful!');
        navigate(`/wallet/${cryptoType}`);
      } else {
        toast.error(data.error || 'Transaction failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate(`/wallet/${cryptoType}`)} className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-6 transition">
          <ArrowLeft size={20} /> Back
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Send className="text-orange-600" size={28} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Send {cryptoType}</h1>
          </div>

          <form onSubmit={handleSubmitForm} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{cryptoType} Amount</label>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.0001"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Wallet ID / Address</label>
              <input
                type="text"
                value={walletID}
                onChange={(e) => setWalletID(e.target.value)}
                placeholder="Wallet Address"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            <button type="submit" className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition shadow-sm">
              Continue
            </button>
          </form>
        </div>

        {/* PIN Modal */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPinModal(false)}>
            <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowPinModal(false)} className="ml-auto block text-slate-400 hover:text-slate-600 mb-4 text-2xl">
                ✕
              </button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-blue-600" size={28} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Enter PIN</h2>
                <p className="text-slate-600">Confirm transaction with your 6-digit PIN</p>
              </div>

              <form onSubmit={handleSubmitPin} className="space-y-6">
                <input
                  type="password"
                  maxLength="6"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="••••••"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl text-center text-2xl tracking-widest focus:ring-2 focus:ring-blue-500 outline-none"
                  autoFocus
                  required
                />
                <button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 shadow-sm flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm'
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
